import type { Entry, EntryId, Match, MatchEntry, Round, TournamentState } from "./types";

/**
 * Implementation of the tournament state converter
 *
 * This class converts between TournamentState objects and an ASCII text representation.
 *
 * The ASCII format is a human-readable text representation of a tournament state with the following structure:
 *
 * 1. : `Entries: name1(seed1) {metadata1} name2(seed2) {metadata2} ...`
 *    - Each entry is formatted as `name(seed)` where name is the player/team identifier and seed is their seeding number
 *    - Optional entry metadata is enclosed in curly braces after the seed: `{points: 10, group: "A"}`
 *
 * 2. Match rounds (1+): `Round n: [entry1 vs entry2, {metadata}] [entry3 vs entry4] ...`
 *    - Each match is enclosed in square brackets: `[...]`
 *    - Entries are separated by " vs "
 *    - Winners are marked with an asterisk: `id*`
 *    - Null/empty entries are represented by an underscore: `_`
 *    - Optional match metadata is enclosed in curly braces after a comma: `{key1: value1, key2: "value2"}`
 *
 * 3. Winners: `Winners: [id1, {metadata1}] [id2, {metadata2}] ...`
 *    - Lists the winners of the tournament with optional metadata
 *    - Each winner is represented by their ID and optional metadata
 *
 * 4. Metadata: `Metadata: {key1: value1, key2: "value2", ...}`
 *    - Competition-level metadata that persists between transformations
 *    - Used to store format-specific state information
 *    - Examples include transformation count, timestamps, and format version
 *
 * Example with entry metadata:
 * ```
 * Entries: Alice(1) {"points": 10, "group": "A"} Bob(2) {"points": 5, group: "B"} Charlie(3) {"points": 8, group: "A"} Dave(4) {"points": 3, "group": "B"}
 * Round 1: [Alice* vs Dave, {"court": 1}] [Bob* vs Charlie]
 * Round 2: [Alice* vs Bob]
 * Winners: Alice Bob
 * Metadata: {"transformCount": 5, "lastTransformed": "2025-05-17T04:30:00.000Z", "formatVersion": "1.0.0"}
 * ```
 *
 * ## Entry Metadata Usage
 *
 * Entry metadata is particularly useful for tournament formats that need to track
 * entry-specific information, such as:
 *
 * - Points earned in a Round Robin format
 * - Group/division assignments
 * - Win/loss/draw statistics
 * - Tiebreaker information
 * - Ranking or standing within a group
 *
 * Example usage:
 * ```typescript
 * // Create a state with entry metadata
 * const state: TournamentState = {
 *   entries: [
 *     ["entry1", 1, { "points": 10, "group": "A", "wins": 3, "losses": 1 }],
 *     ["entry2", 2, { "points": 7, "group": "A", "wins": 2, "losses": 2 }],
 *     ["entry3", 3, { "points": 12, "group": "B", "wins": 4, "losses": 0 }],
 *   ],
 *   rounds: [...],
 * };
 *
 * // Convert to ASCII representation
 * const ascii = tournamentStateConverter.stateToAscii(state);
 *
 * // Parse back from ASCII
 * const parsedState = tournamentStateConverter.asciiToState(ascii);
 * ```
 *
 * The metadata section allows tournament formats to store persistent information between transformations.
 * This is useful for tracking:
 * - Number of transformations applied
 * - Timestamps of transformations
 * - Format version information
 * - Division assignments
 * - Round progression information
 * - Entry-specific statistics and groupings
 * - Any other format-specific state
 */
export class TournamentStateConverter {
  stateToAscii(state: TournamentState): string {
    if (!state) return "";

    let result = "";

    // Process entries round (round 0)
    const entries = state.entries;
    if (entries.length > 0) {
      const entriesAscii = `Entries: ${entries
        .map((entry) => {
          const [id, seed, metadata] = entry;
          let entryStr = `${id}(${seed})`;

          // Add metadata if present
          if (metadata && Object.keys(metadata).length > 0) {
            const metadataStr = JSON.stringify(metadata);
            entryStr += ` ${metadataStr}`;
          }

          return entryStr;
        })
        .join(" ")}`.trim();
      result += `${entriesAscii}\n`;
    }

    // Process competition metadata if present
    if (state.metadata && Object.keys(state.metadata).length > 0) {
      const metadataStr = JSON.stringify(state.metadata);
      result += `Metadata: ${metadataStr}\n`;
    }

    // Process match rounds
    for (const [i, round] of state.rounds.entries()) {
      if (!round || !round.length) continue;

      // Add round header
      result += `Round ${i + 1}: ${this.matchesToAscii(round)}\n`;
    }

    if (state.winners && state.winners.length > 0) {
      const winnersStr = state.winners
        .map(([id, metadata]) => {
          if (!metadata || Object.keys(metadata).length === 0) {
            return id;
          }
          return `[${id}, ${JSON.stringify(metadata)}]`;
        })
        .join(" ");
      result += `Winners: ${winnersStr}\n`;
    }

    return result.trim();
  }

  /**
   * Converts an array of Match objects to their ASCII representation
   *
   * @param matches - Array of matches to convert
   * @returns ASCII string representation of the matches
   *
   * In the ASCII format, matches are represented as:
   * `[entry1 vs entry2, {metadata}]`
   * - Entries are formatted as ID with optional * for winners
   * - Null entries are represented as _
   * - Multiple matches are separated by spaces
   */
  private matchesToAscii(matches: Match[]): string {
    return matches
      .map((match) => {
        const entry1 = match[0];
        const entry2 = match[1];
        const metadata = match[2];

        // Format as [entry1 vs entry2]
        let result = `[${this.entryToAscii(entry1)} vs ${this.entryToAscii(entry2)}`;

        // Add metadata if it exists and has properties
        // In ASCII format, metadata appears after entries as {key1: value1, key2: "value2"}
        if (metadata && Object.keys(metadata).length > 0) {
          // Format the metadata as a string
          const metadataStr = JSON.stringify(metadata);

          if (metadataStr) {
            result += `, ${metadataStr}`;
          }
        }

        result += "]";
        return result;
      })
      .join(" "); // Matches are separated by spaces in ASCII format
  }

  /**
   * Converts a match entry to its ASCII representation
   *
   * @param entry - The match entry to convert, or null for empty entries
   * @returns ASCII string representation of the entry
   *
   * In the ASCII format:
   * - Null entries are represented as "_"
   * - Regular entries are represented by their ID
   * - Winning entries have an asterisk appended: "id*"
   */
  private entryToAscii(entry: MatchEntry | null): string {
    if (!entry) return "_"; // Null entries are represented as underscore
    const [id, isWinner] = entry;
    return `${id}${isWinner ? "*" : ""}`; // Add asterisk for winners
  }

  /**
   * Converts an ASCII string representation back to a TournamentState object
   *
   * @param ascii - The ASCII string to parse
   * @returns The parsed TournamentState object
   *
   * This method parses the ASCII format with the following structure:
   * - Lines starting with "Entries:" contain initial entries in format "name(seed)"
   * - Lines starting with "Round n:" (n > 0) contain matches in format "[entry1 vs entry2, {metadata}]"
   */
  asciiToState(ascii: string): TournamentState {
    if (!ascii.trim()) return { entries: [], rounds: [], metadata: {} };

    const lines = ascii
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    let entries: Entry[] = [];
    const rounds: Round[] = [];
    let winners: [EntryId, Record<string, any>][] = [];
    let metadata: Record<string, any> = {};

    const entriesRegex = /^Entries:\s*(.*)/;
    const roundRegex = /^Round (\d+):\s*(.*)/;
    const winnersRegex = /^Winners:\s*(.*)/;
    const metadataRegex = /^Metadata:\s*(.*)/;

    for (const line of lines) {
      if (entriesRegex.test(line)) {
        const entriesMatch = line.match(entriesRegex);
        if (!entriesMatch) continue;
        const entriesContent = entriesMatch[1].trim();
        // Process entries
        // In ASCII format, entries are represented as "name(seed)" or "name(seed) {metadata}"
        entries = [];
        const entryParts = [];

        let currentPart = "";
        let inBraces = 0;
        let seenSpace = false;

        for (let i = 0; i < entriesContent.length; i++) {
          const char = entriesContent[i];

          if (char === "{") {
            inBraces++;
            currentPart += char;
          } else if (char === "}") {
            inBraces--;
            currentPart += char;
          } else if (char === " " && inBraces === 0) {
            seenSpace = true;
          } else if (inBraces === 0 && seenSpace) {
            entryParts.push(currentPart.trim());
            currentPart = char;
            seenSpace = false;
            inBraces = 0;
          } else {
            currentPart += char;
          }
        }

        if (currentPart.trim()) {
          entryParts.push(currentPart.trim());
        }
        // Process each entry part
        for (const entryPart of entryParts) {
          // Consume the entry up to a brace or end of string:
          const firstBraceIndex = entryPart.indexOf("{");
          const entryStr = firstBraceIndex !== -1 ? entryPart.slice(0, firstBraceIndex) : entryPart;
          const entryMetadata = firstBraceIndex !== -1 ? entryPart.slice(firstBraceIndex) : "";
          const entryMetadataTrimmed = entryMetadata.trim();
          const entryMetadataParsed = entryMetadataTrimmed ? JSON.parse(entryMetadataTrimmed) : {};

          // Parse "name(seed)" format
          const match = entryStr.match(/^([^(]+)\((\d+)\)$/);
          if (match) {
            entries.push([match[1], Number.parseInt(match[2], 10), entryMetadataParsed]);
          }
        }
      }

      if (roundRegex.test(line)) {
        const roundMatch = line.match(/^Round (\d+):\s*(.*)/);
        if (!roundMatch) continue;

        const roundNumber = Number.parseInt(roundMatch[1], 10);
        const roundContent = roundMatch[2].trim();

        if (roundNumber === 0) {
        } else {
          // Process matches (Round 1+)
          // In ASCII format, matches are enclosed in square brackets: [entry1 vs entry2, {metadata}]
          const matches: Match[] = [];

          // Use regex to extract content between square brackets
          // This handles the [entry1 vs entry2, {metadata}] format
          const matchRegex = /\[(.*?)\]/g;
          let match: RegExpExecArray | null = matchRegex.exec(roundContent);

          while (match !== null) {
            const matchContent = match[1].trim();
            matches.push(this.parseMatch(matchContent));
            match = matchRegex.exec(roundContent);
          }

          rounds[roundNumber - 1] = matches;
        }
      } else if (winnersRegex.test(line)) {
        // Process winners line
        const winnersMatch = line.match(winnersRegex);
        if (winnersMatch) {
          const winnersContent = winnersMatch[1].trim();
          winners = this.parseWinners(winnersContent);
        }
      } else if (metadataRegex.test(line)) {
        // Process metadata line
        const metadataMatch = line.match(metadataRegex);
        if (metadataMatch) {
          const metadataStr = metadataMatch[1].trim();
          if (metadataStr.startsWith("{") && metadataStr.endsWith("}")) {
            metadata = JSON.parse(metadataStr);
          } else {
            metadata = {};
          }
        }
      }
    }

    const state = { entries, rounds, winners, metadata };
    return state;
  }

  /**
   * Parses a match string from the ASCII format into a Match object
   *
   * @param matchStr - The match string to parse (e.g., "entry1 vs entry2, {metadata}")
   * @returns The parsed Match object
   *
   * In the ASCII format, matches have the structure:
   * - "entry1 vs entry2" for basic matches
   * - "entry1 vs entry2, {key1: value1, key2: "value2"}" for matches with metadata
   */
  private parseMatch(matchStr: string): Match {
    // Extract metadata if present
    // In ASCII format, metadata follows the entries after a comma: "entry1 vs entry2, {metadata}"
    let metadata: Record<string, any> = {};
    const metadataMatch = matchStr.match(/(.*),\s*(\{.*\})/);
    let matchStrWithoutMetadata = matchStr;

    if (metadataMatch) {
      matchStrWithoutMetadata = metadataMatch[1].trim();
      try {
        // Replace "division: "A"" with "division": "A" to make it valid JSON
        // This converts the ASCII metadata format to proper JSON
        const jsonStr = metadataMatch[2].replace(/(\w+):/g, '"$1":');
        metadata = JSON.parse(jsonStr);
      } catch (e) {
        console.error("Failed to parse metadata:", e);
      }
    }

    // Parse entries - entries are separated by " vs " in the ASCII format
    const vsMatch = matchStrWithoutMetadata.match(/^(.*?)\s+vs\s+(.*?)$/);
    if (!vsMatch) return [null, null, metadata];

    const entry1 = this.parseEntry(vsMatch[1].trim());
    const entry2 = this.parseEntry(vsMatch[2].trim());

    return [entry1, entry2, metadata];
  }

  /**
   * Parses an entry string from the ASCII format into a MatchEntry object
   *
   * @param entryStr - The entry string to parse (e.g., "player1", "player2*", "_")
   * @returns The parsed MatchEntry object or null for empty entries
   *
   * In the ASCII format:
   * - "_" represents a null/empty entry
   * - "id" represents a regular entry
   * - "id*" represents a winning entry (with asterisk suffix)
   */
  /**
   * Parses a winners string from the ASCII format into an array of winner entries
   *
   * @param winnersStr - The winners string to parse (e.g., "id1 id2" or "[id1, {meta1}] [id2, {meta2}]")
   * @returns Array of winner entries with metadata
   */
  private parseWinners(winnersStr: string): [EntryId, Record<string, any>][] {
    const winners: [EntryId, Record<string, any>][] = [];
    const regex = /\[([^,]+),\s*(\{[^}]+\})\]|(\S+)/g;
    let match: RegExpExecArray | null = regex.exec(winnersStr);

    while (match !== null) {
      if (match[3]) {
        // Simple format without metadata
        winners.push([match[3], {}]);
      } else {
        // Format with metadata
        const id = match[1].trim();
        try {
          // Convert property names to quoted strings for valid JSON
          const metadataStr = match[2].replace(/(\w+):/g, '"$1":');
          const metadata = JSON.parse(metadataStr);
          winners.push([id, metadata]);
        } catch (e) {
          console.error("Failed to parse winner metadata:", e);
          winners.push([id, {}]);
        }
      }
      match = regex.exec(winnersStr);
    }

    return winners;
  }

  private parseEntry(entryStr: string): MatchEntry | null {
    if (entryStr === "_") return null; // Underscore represents null entries in ASCII format

    const isWinner = entryStr.endsWith("*"); // Asterisk suffix indicates winner
    const id = isWinner ? entryStr.slice(0, -1) : entryStr; // Remove asterisk if present

    return [id, isWinner];
  }
  /**
   * Helper method to stringify any JavaScript value, including nested objects and arrays
   *
   * @param value - The value to stringify
   * @returns A string representation of the value
   */
  private stringifyValue(value: any): string {
    if (value === null || value === undefined) {
      return "null";
    }

    if (typeof value === "string") {
      return `"${value}"`;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    if (Array.isArray(value)) {
      const items = value.map((item) => this.stringifyValue(item)).join(", ");
      return `[${items}]`;
    }

    if (typeof value === "object") {
      const entries = Object.entries(value)
        .map(([k, v]) => `"${k}": ${this.stringifyValue(v)}`)
        .join(", ");
      return `{${entries}}`;
    }

    // Fallback for any other types
    return String(value);
  }
}

// Export a singleton instance
export const tournamentStateConverter = new TournamentStateConverter();
